import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { getTicketTypeByIds } from "app/containers/Features/containers/TeamSettings/container/Projects/container/WorkItems/helper";
import { IEffortActual } from "app/containers/Features/containers/Ticket/types";
import {
  selectActiveTimerForm,
  selectEffortActualRequestLoading,
  selectEnableTimeTrackerPreview,
  selectTimeTrackerForm,
} from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import {
  ISelectedTicketDetails,
  ITimeTrackerForm,
} from "app/containers/Features/containers/TimeSheet/types";
import {
  formatEffortShorthand,
  parseEffortInput,
} from "app/containers/Features/helper";
import { selectTicketTypes } from "app/redux/selector";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dateFormatter } from "utils/helpers";
import { useTimeTracker } from "utils/hooks/useTimeTracker";

export const useUserTimeInputCardLogic = ({
  selectedTicketDetail,
  assigneeId,
  callback,
}: {
  selectedTicketDetail: ISelectedTicketDetails;
  assigneeId?: string;
  callback?: (data: {
    form: ITimeTrackerForm;
    response: IEffortActual;
  }) => void;
}) => {
  // hooks
  const { formattedTime, toggle, handleTimerStart, isRunning } =
    useTimeTracker();
  const dispatch = useDispatch();

  // Global States
  const timeTrackerForm = useSelector(selectTimeTrackerForm);
  const activeTimerForm = useSelector(selectActiveTimerForm);
  const projectMemberList = useSelector(selectProjectMemberList);
  const enablePreview = useSelector(selectEnableTimeTrackerPreview);
  const effortActualRequestLoading = useSelector(
    selectEffortActualRequestLoading,
  );
  const ticketTypeIds = useSelector(selectTicketTypes);

  // Local States
  const [effortInput, setEffortInput] = useState<string>("");

  // Variables
  const parsedEffort = parseEffortInput(effortInput);
  const formattedEffort = formatEffortShorthand(
    timeTrackerForm.hours,
    timeTrackerForm.minutes,
  );
  const timerRunning =
    isRunning &&
    activeTimerForm.selectedTicketDetails._id === selectedTicketDetail?._id;
  const typeData = getTicketTypeByIds(
    ticketTypeIds,
    selectedTicketDetail?.typeIcon || "",
  )[0];

  // Functions
  const handleSubmit = () => {
    dispatch(
      timeSheetActions.setTimeTrackerForm({
        ...timeTrackerForm,
        selectedTicketDetails: {
          _id: selectedTicketDetail?._id,
          ticketName: selectedTicketDetail?.ticketName,
          projectId: selectedTicketDetail.projectId,
        },
      }),
    );

    dispatch(
      timeSheetActions.effortActualRequest({
        callback: ({ response, form }) => {
          setEffortInput("");
          dispatch(timeSheetActions.setEnableTimeTrackerPreview(false));
          if (callback) callback({ form, response });
        },
      }),
    );
  };

  const handlePreview = () => {
    if (!parsedEffort.isValid) return;

    const end = new Date(timeTrackerForm.endTime || Date.now());

    const durationSeconds =
      parsedEffort.hours * 3600 + parsedEffort.minutes * 60;

    const start = new Date(end.getTime() - durationSeconds * 1000);

    dispatch(
      timeSheetActions.setTimeTrackerForm({
        ...timeTrackerForm,
        hours: parsedEffort.hours,
        minutes: parsedEffort.minutes,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        date: dateFormatter(start, "onlyDate"),
      }),
    );

    dispatch(timeSheetActions.setEnableTimeTrackerPreview(true));
  };

  return {
    formattedTime,
    toggle,
    handleTimerStart,
    isRunning,
    timeTrackerForm,
    activeTimerForm,
    projectMemberList,
    enablePreview,
    effortActualRequestLoading,
    ticketTypeIds,
    effortInput,
    setEffortInput,
    parsedEffort,
    formattedEffort,
    timerRunning,
    typeData,
    handleSubmit,
    handlePreview,
    dispatch,
  };
};
